'use client';

import { Icon, Notification, SkeletonBar } from '@petsmart-ui/sparky';
import { useEffect, useState } from 'react';
import { useCookie } from 'react-use';

import { useCart, useIsMounted } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';

import { trackNavigationHeaderClick } from '../../shared';
import './cart-button.scss';

type CartButtonProps = {
    showCartStatus?: boolean;
};

export function CartButton({ showCartStatus = true }: CartButtonProps) {
    const { cart: cartState, isLoading: isCartLoading } = useCart();
    const { isMounted } = useIsMounted();

    const [cachedQty, updateCachedQty] = useCookie('cart_quantity');
    const [qty, setQty] = useState(cachedQty ? Number(cachedQty) : 0);

    function handleClick() {
        trackNavigationHeaderClick({
            selection: 'cart',
            linkUrl: NAVIGATION_URLS.legacy.cart,
        });
    }

    useEffect(() => {
        if (isCartLoading) return;

        const currentQty = cartState?.summary?.itemCount ?? 0;

        setQty(currentQty);
        updateCachedQty(String(currentQty));
    }, [cartState?.summary?.itemCount, isCartLoading, updateCachedQty]);

    if (showCartStatus && (!isMounted || isCartLoading)) {
        return <SkeletonBar className="cart-button__loader" />;
    }

    return (
        <div className="cart-button">
            <a
                href={NAVIGATION_URLS.legacy.cart}
                className="cart-button__link sparky-c-button--bare sparky-c-button"
                aria-label="Shopping Cart"
                title="View Cart"
                onClick={handleClick}
            >
                <Icon size="lg" name="cart-outline" />

                {showCartStatus && Boolean(qty) && (
                    <Notification
                        text={qty}
                        data-testid="notification"
                        className="cart-button__notification"
                        screenReaderText={`${qty} items in your cart`}
                    />
                )}
            </a>
        </div>
    );
}
